import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { CommandPalette } from "./CommandPalette";
import { CallDock } from "./CallDock";
import { ChatDock } from "./ChatDock";
import { AskPegasusPopup } from "@/features/assistant/AskPegasusPopup";
import { SubResendPrompt } from "@/features/jobs/SubResendPrompt";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useSyncEquipmentCategories } from "@/features/settings/equipmentCategories";

/**
 * The CRM shell. Every feature route renders inside <Outlet />.
 * Layout = persistent chrome (sidebar nav + top header).
 * Content area is scrollable independently of the chrome.
 *
 * The routed content is wrapped in an ErrorBoundary keyed by pathname: a crash
 * in one screen shows a localized message (the sidebar/header survive), and
 * navigating to another route resets it.
 *
 * Ctrl/Cmd+K opens the command palette from anywhere (DESIGN-SPRINT-01) —
 * a modified shortcut, so it works even while typing in a field.
 */
export function Layout() {
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  // ADMIN-EDIT-01: keep the equipment-classifier's label/grouping registry in
  // sync with the admin-managed equipment_category table.
  useSyncEquipmentCategories();

  // DESKTOP-GLASS-01: mark the page as the office, so the glass tokens and
  // the calm-card rules in index.css apply — on <html>, because menus render
  // in portals outside this tree. The tech app (/m) never sets it.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.shell = "office";
    return () => {
      delete root.dataset.shell;
    };
  }, []);

  // The top bar floats over <main> (glass). It stops at main's scrollbar:
  // --main-sbw = the scrollbar's width, re-measured whenever it comes or goes.
  const colRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const col = colRef.current;
    const main = mainRef.current;
    if (!col || !main) return;
    const measure = () => col.style.setProperty("--main-sbw", `${main.offsetWidth - main.clientWidth}px`);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(main);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div data-shell-root className="flex h-screen w-screen overflow-hidden bg-canvas">
      <Sidebar />
      <div ref={colRef} className="relative flex flex-1 flex-col overflow-hidden">
        <Header onOpenPalette={() => setPaletteOpen(true)} />
        {/* DESIGN-CALM-01: data-office scopes the office type floor (index.css).
            DESKTOP-GLASS-01: pt-16 = the floating top bar's height — the page
            starts below it and scrolls up under its frost; scroll-pt-16 keeps
            a jump-to target from landing under the bar. */}
        <main ref={mainRef} className="flex-1 overflow-y-auto pt-16 scroll-pt-16" data-office>
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      {/* The floating docks never print (PARTS-PRINT-01). `contents` = no box
          of its own on screen; the docks stay fixed exactly as before. */}
      <div className="contents print:hidden">
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        <CallDock />
        {/* ASK-POPUP-01: Ask Pegasus rides on every page (Seth: "easily
            accessible on all pages … a pop up so you don't have to leave"). */}
        <AskPegasusPopup />
        {/* CHAT-01: private team messages — the bubble beside Ask Pegasus. */}
        <ChatDock />
        {/* SUB-AUTO-RESEND: after an install the sub already has is moved or
            unscheduled — anywhere in the app — ask whether to tell them. */}
        <SubResendPrompt />
      </div>
    </div>
  );
}
